import { pageEnabledGuard } from "./navigation/page-enabled.guard";
import { flightEntryGuard } from "./navigation/flight-entry.guard";
import { baseSelectionGuard } from "./navigation/base-selection.guard";
import { NgModule } from "@angular/core";
import { PreloadAllModules, Route, RouterModule, Routes } from "@angular/router";
import { webAuthGuard } from "./services/auth/web-auth.guard";

const guarded = (route: Route): Route => ({
  ...route,
  canMatch: [...(route.canMatch ?? []), webAuthGuard, pageEnabledGuard],
  canActivate: [...(route.canActivate ?? []), baseSelectionGuard],
  runGuardsAndResolvers: "always",
});

const routes: Routes = [
  {
    path: "",
    redirectTo: "login",
    pathMatch: "full",
  },
  {
    path: "login",
    loadChildren: () => import("./pages/login/login.module").then((m) => m.LoginPageModule),
  },
  // Med-crew screen: no side-menu entry, reached by URL only.
  guarded({
    path: "med-crew",
    loadChildren: () => import("./pages/med-crew/med-crew-page.module").then((m) => m.MedCrewPageModule),
  }),
  guarded({
    path: "settings",
    loadChildren: () => import("./pages/settings/settings.module").then((m) => m.SettingsPageModule),
  }),
  guarded({
    path: "updates",
    loadChildren: () => import("./pages/updates/updates.module").then((m) => m.UpdatesPageModule),
  }),
  guarded({
    path: "file-system",
    loadChildren: () => import("./pages/file-system/file-system.module").then((m) => m.FileSystemPageModule),
  }),
  guarded({
    path: "updates-detail",
    loadChildren: () => import("./pages/updates-detail/updates-detail.module").then((m) => m.UpdatesDetailPageModule),
  }),
  guarded({
    path: "search",
    loadChildren: () => import("./pages/search/search.module").then((m) => m.SearchPageModule),
  }),
  guarded({
    path: "flow-briefing",
    loadChildren: () => import("./pages/flow-briefing/flow-briefing.module").then((m) => m.FlowBriefingPageModule),
  }),
  guarded({
    path: "flow-briefing-auto",
    loadChildren: () =>
      import("./pages/flow-briefing-auto/flow-briefing-auto.module").then((m) => m.FlowBriefingAutoPageModule),
  }),
  guarded({
    path: "flow-briefing-documents",
    loadChildren: () =>
      import("./pages/flow-briefing-documents/flow-briefing-documents.module").then(
        (m) => m.FlowBriefingDocumentsPageModule
      ),
  }),
  guarded({
    path: "flow-briefing-notams",
    loadChildren: () =>
      import("./pages/flow-briefing-notams/flow-briefing-notams.module").then((m) => m.FlowBriefingNotamsPageModule),
  }),
  guarded({
    path: "flow-briefing-weather",
    loadChildren: () =>
      import("./pages/flow-briefing-weather/flow-briefing-weather.module").then((m) => m.FlowBriefingWeatherPageModule),
  }),
  guarded({
    path: "flow-briefing-webcams",
    loadChildren: () =>
      import("./pages/flow-briefing-webcams/flow-briefing-webcams.module").then((m) => m.FlowBriefingWebcamsPageModule),
  }),
  guarded({
    path: "coordinates",
    loadChildren: () => import("./pages/coordinates/coordinates.module").then((m) => m.CoordinatesPageModule),
  }),
  guarded({
    path: "map",
    loadChildren: () => import("./pages/map/map.module").then((m) => m.MapPageModule),
  }),
  guarded({
    path: "base-briefing",
    loadChildren: () => import("./pages/base-briefing/base-briefing.module").then((m) => m.BaseBriefingPageModule),
  }),
  guarded({
    path: "flight-timer-list",
    loadChildren: () =>
      import("./pages/flight-timer-list/flight-timer-list.module").then((m) => m.FlightTimerListPageModule),
  }),
  guarded({
    path: "edit-add-flightplan",
    canActivate: [flightEntryGuard],
    loadChildren: () =>
      import("./pages/edit-add-flightplan/edit-add-flightplan.module").then((m) => m.EditAddFlightplanPageModule),
  }),
  guarded({
    path: "preview-ifps-flightplan",
    loadChildren: () =>
      import("./pages/preview-ifps-flightplan/preview-ifps-flightplan.module").then(
        (m) => m.PreviewIfpsFlightplanPageModule
      ),
  }),
  guarded({
    path: "add-menu",
    canActivate: [flightEntryGuard],
    loadChildren: () => import("./pages/add-menu/add-menu.module").then((m) => m.AddMenuPageModule),
  }),
  guarded({
    path: "quick-docs",
    loadChildren: () => import("./pages/quick-docs/quick-docs.module").then((m) => m.QuickDocsPageModule),
  }),
  guarded({
    path: "mission-page",
    loadChildren: () => import("./pages/mission-page/mission-page.module").then((m) => m.MissionPagePageModule),
  }),
  guarded({
    path: "md-preview",
    loadChildren: () => import("./pages/md-preview/md-preview.module").then((m) => m.MdPreviewPageModule),
  }),
  guarded({
    path: "duty-ofp-limits",
    loadChildren: () => import("./pages/duty-ofp-limits/duty-ofp-limits.module").then((m) => m.DutyOfpLimitsPageModule),
  }),
  guarded({
    path: "notifications",
    loadChildren: () => import("./pages/notifications/notifications.module").then((m) => m.NotificationsPageModule),
  }),
  guarded({
    path: "add-edit-flightrecord",
    loadChildren: () =>
      import("./pages/add-edit-flightrecord/add-edit-flightrecord.module").then((m) => m.AddEditFlightrecordPageModule),
  }),
  guarded({
    path: "logbook",
    loadChildren: () => import("./pages/logbook/logbook.module").then((m) => m.LogbookPageModule),
  }),
  // Auth flow pages, ported from `main` (MR !1547). Deliberately NOT `guarded()`:
  // every one of them runs BEFORE a session exists, so a guard here locks the
  // user out of the flow that would create the session. `main` guarded only
  // `verify/code`; that is dropped, because MFA verification happens mid-login.
  {
    path: "login/code",
    loadChildren: () => import("./pages/login-code/login-code.module").then((m) => m.LoginCodePageModule),
  },
  {
    path: "verify/code",
    loadChildren: () => import("./pages/verify-code/verify-code.module").then((m) => m.VerifyCodePageModule),
  },
  {
    path: "confirm",
    loadChildren: () => import("./pages/confirm/confirm.module").then((m) => m.ConfirmPageModule),
  },
  {
    path: "confirm/resend",
    loadChildren: () => import("./pages/confirm-resend/confirm-resend.module").then((m) => m.ConfirmResendPageModule),
  },
  {
    path: "password/reset",
    loadChildren: () => import("./pages/password-reset/password-reset.module").then((m) => m.PasswordResetPageModule),
  },
  {
    path: "password/new",
    loadChildren: () => import("./pages/password-new/password-new.module").then((m) => m.PasswordNewPageModule),
  },
  {
    path: "unlock/request",
    loadChildren: () => import("./pages/unlock-request/unlock-request.module").then((m) => m.UnlockRequestPageModule),
  },
  {
    path: "unlock",
    loadChildren: () => import("./pages/unlock/unlock.module").then((m) => m.UnlockPageModule),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      scrollPositionRestoration: "enabled",
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
